/**
 * Dropdown — generic action menu following the Tailwind UI "Simple" pattern.
 *
 * Two trigger modes:
 *   1. `label` prop  → built-in ring-1 button (Tailwind UI Simple style)
 *   2. `renderTrigger` prop → fully custom button, receives all ARIA + event
 *      props already wired up. Use this for icon-only buttons or triggers
 *      that show the current selection.
 *
 * Items can carry `description` (subtitle), `icon`, `checked` (checkmark),
 * and `variant="danger"`. Group them with `groups`; adjacent groups get a
 * hairline divider between them.
 *
 * `role` prop ("menu" default, or "listbox"): a `role="menu"` panel tells
 * assistive tech "these are commands" (Tailwind UI's own semantics — correct
 * for action menus like the roster row's "…" button). A single-select
 * picker (choose one value from a set — see `components/ui/Select.tsx`) is
 * a different widget and needs `role="listbox"` + `role="option"` +
 * `aria-selected` so screen readers announce "selected", not "activate".
 * Don't pass `role="listbox"` for a menu of actions.
 *
 * The panel always renders in a portal, positioned by `useAnchoredPanel` —
 * which also owns the flip-up-when-cramped, the height clamp, the
 * outside-pointerdown dismissal and the capture-phase Escape. See that hook
 * for why a portal is required rather than cosmetic.
 */

import { useAnchoredPanel } from "@hooks/useAnchoredPanel"
import { cn } from "@lib/cn"
import { Check, ChevronDown } from "lucide-react"
import { type ReactNode, type RefObject, useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"

// ─── Public types ────────────────────────────────────────────────────────────

export interface DropdownItem {
  label: string
  /** Optional subtitle rendered below the label in muted text. */
  description?: string
  /** Leading icon — rendered at 16 × 16 px. */
  icon?: ReactNode
  /** When true a Check icon appears on the trailing edge (selected state). */
  checked?: boolean
  /**
   * Right-aligned adornment rendered before the check — a result count on a
   * filter option, a keyboard shortcut hint, a badge. Muted and tabular by
   * default so a column of numbers lines up.
   */
  trailing?: ReactNode
  /** "danger" renders the item in red for destructive actions. */
  variant?: "default" | "danger"
  disabled?: boolean
  onClick: () => void
}

export interface DropdownGroup {
  items: DropdownItem[]
}

/** Props injected into custom trigger elements via `renderTrigger`. */
export interface DropdownTriggerProps {
  ref: RefObject<HTMLButtonElement>
  open: boolean
  "aria-haspopup": "menu" | "listbox"
  "aria-expanded": boolean
  onClick: () => void
  onKeyDown: (e: React.KeyboardEvent) => void
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  /**
   * Text label rendered inside the built-in Tailwind UI-style button.
   * Mutually exclusive with `renderTrigger`.
   */
  label?: string
  /** Icon rendered left of `label` inside the built-in button. */
  leadingIcon?: ReactNode
  /**
   * Render your own trigger button and receive all required ARIA + event props.
   * Mutually exclusive with `label`.
   *
   * @example
   * renderTrigger={({ ref, open, ...props }) => (
   *   <button ref={ref} type="button" aria-label="More" {...props} className="...">
   *     <MoreVertical className="h-4 w-4" />
   *   </button>
   * )}
   */
  renderTrigger?: (props: DropdownTriggerProps) => ReactNode
  groups: DropdownGroup[]
  /** Which edge of the trigger the panel aligns to. Default "right". */
  align?: "left" | "right"
  /**
   * ARIA semantics for the panel + items. "menu"/"menuitem" (default) for
   * action menus; "listbox"/"option" for single-select pickers. See the
   * module doc comment above.
   */
  role?: "menu" | "listbox"
  /** Extra classes on the outermost trigger wrapper `div`. */
  className?: string
  /** Extra classes on the portalled panel. Use to match the trigger's width. */
  panelClassName?: string
}

export function Dropdown({
  label,
  leadingIcon,
  renderTrigger,
  groups,
  align = "right",
  role = "menu",
  className,
  panelClassName,
}: Props) {
  const [open, setOpen] = useState(false)
  const [focusedIdx, setFocusedIdx] = useState(-1)
  const { triggerRef, panelRef, panelStyle, triggerWidth, close } = useAnchoredPanel({
    open,
    onClose: () => setOpen(false),
    align,
  })
  const itemRole = role === "listbox" ? "option" : "menuitem"

  // Flat item list for keyboard navigation — kept stable via useMemo.
  const flatItems = useMemo(() => groups.flatMap((g) => g.items), [groups])

  // Pre-compute per-group start offset once so the render loop is clean.
  const sections = useMemo(() => {
    let offset = 0
    return groups.map((group) => {
      const start = offset
      offset += group.items.length
      return { items: group.items, start }
    })
  }, [groups])

  // Reset keyboard focus index on close.
  useEffect(() => {
    if (!open) setFocusedIdx(-1)
  }, [open])

  // Keep the arrow-key cursor inside the scrollable panel. Focus itself stays
  // on the trigger (the panel is `tabIndex={-1}`), so nothing here steals it —
  // `block: "nearest"` only scrolls when the item is actually out of view.
  useEffect(() => {
    if (!open || focusedIdx < 0) return
    panelRef.current
      ?.querySelector<HTMLElement>(`[data-dropdown-index="${focusedIdx}"]`)
      ?.scrollIntoView({ block: "nearest" })
  }, [open, focusedIdx, panelRef])

  const toggle = () => setOpen((o) => !o)

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault()
        setOpen(true)
      }
      return
    }

    switch (e.key) {
      case "Escape":
        e.preventDefault()
        close()
        break

      case "ArrowDown": {
        e.preventDefault()
        setFocusedIdx((i) => {
          for (let n = i + 1; n < flatItems.length; n++) {
            if (!flatItems[n].disabled) return n
          }
          return i
        })
        break
      }

      case "ArrowUp": {
        e.preventDefault()
        setFocusedIdx((i) => {
          for (let n = i - 1; n >= 0; n--) {
            if (!flatItems[n].disabled) return n
          }
          return i
        })
        break
      }

      case "Enter":
      case " ": {
        if (focusedIdx >= 0) {
          e.preventDefault()
          const item = flatItems[focusedIdx]
          if (!item.disabled) {
            item.onClick()
            close()
          }
        }
        break
      }

      case "Tab":
        setOpen(false)
        break
    }
  }

  const triggerProps: DropdownTriggerProps = {
    ref: triggerRef,
    open,
    "aria-haspopup": role,
    "aria-expanded": open,
    onClick: toggle,
    onKeyDown: handleKeyDown,
  }

  return (
    <div className={cn("relative w-fit text-left", className)}>
      {/* ── Trigger ─────────────────────────────────────────────────────── */}
      {renderTrigger ? (
        renderTrigger(triggerProps)
      ) : (
        <button
          ref={triggerRef}
          type="button"
          aria-haspopup={role}
          aria-expanded={open}
          onClick={toggle}
          onKeyDown={handleKeyDown}
          className="rounded-control bg-surface text-ink border-border hover:bg-inset inline-flex min-h-11 items-center gap-x-1.5 border px-3 text-sm font-semibold md:min-h-9"
        >
          {leadingIcon && (
            <span className="text-ink-subtle" aria-hidden>
              {leadingIcon}
            </span>
          )}
          {label}
          <ChevronDown
            className={cn(
              "text-ink-subtle -mr-1 h-5 w-5 transition-transform duration-150",
              open && "rotate-180",
            )}
            aria-hidden
          />
        </button>
      )}

      {/* ── Panel — portalled + fixed-positioned from the trigger's rect ─── */}
      {open &&
        panelStyle &&
        createPortal(
          <div
            ref={panelRef}
            role={role}
            aria-orientation={role === "menu" ? "vertical" : undefined}
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            style={{
              ...panelStyle,
              minWidth: role === "listbox" ? triggerWidth : undefined,
            }}
            className={cn(
              "rounded-card border-border bg-surface shadow-float z-50 min-w-[12rem] border focus:outline-none",
              // The measured `maxHeight` from the hook only bounds the box;
              // without this the overflow is simply hidden past the viewport
              // edge and the last options are unreachable.
              "overflow-y-auto overscroll-contain",
              panelClassName,
            )}
          >
            {sections.map(({ items, start }, gi) => (
              <div key={gi} className={cn("py-1", gi > 0 && "border-hairline border-t")}>
                {items.map((item, ii) => {
                  const idx = start + ii
                  const isFocused = focusedIdx === idx
                  const isDanger = item.variant === "danger"

                  return (
                    <button
                      key={ii}
                      type="button"
                      role={itemRole}
                      data-dropdown-index={idx}
                      aria-selected={role === "listbox" ? item.checked : undefined}
                      disabled={item.disabled}
                      onClick={() => {
                        if (!item.disabled) {
                          // Focus back on the trigger BEFORE the action runs:
                          // the item is about to unmount, and a dialog the
                          // action opens records whatever holds focus now as
                          // the place to return to.
                          triggerRef.current?.focus()
                          item.onClick()
                          setOpen(false)
                        }
                      }}
                      className={cn(
                        "group flex w-full items-start gap-3 px-4 py-2 text-left text-sm",
                        item.disabled && "cursor-not-allowed opacity-40",
                        isDanger
                          ? isFocused
                            ? "bg-danger-soft text-danger-ink outline-none"
                            : "text-danger-ink hover:bg-danger-soft"
                          : isFocused
                            ? "bg-inset text-ink outline-none"
                            : "text-ink-muted hover:bg-inset hover:text-ink",
                      )}
                    >
                      {/* Leading icon */}
                      {item.icon && (
                        <span
                          className={cn(
                            "mt-0.5 h-4 w-4 shrink-0",
                            isDanger
                              ? "text-danger-ink"
                              : "text-ink-subtle group-hover:text-ink-muted",
                          )}
                          aria-hidden
                        >
                          {item.icon}
                        </span>
                      )}

                      {/* Label + optional description */}
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="leading-tight">{item.label}</span>
                        {item.description && (
                          <span className="text-meta mt-0.5">{item.description}</span>
                        )}
                      </span>

                      {/* Trailing adornment — e.g. a result count on a filter
                          option. Sits before the check so the selection
                          indicator stays flush against the panel edge. */}
                      {item.trailing && (
                        <span className="text-ink-subtle mt-0.5 shrink-0 text-xs tabular-nums">
                          {item.trailing}
                        </span>
                      )}

                      {/* Trailing check (selection indicator) */}
                      {item.checked && (
                        <Check className="text-accent mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                      )}
                    </button>
                  )
                })}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  )
}
